// Ported from dev-scrips-pm-react src/shared/types/billing.ts (PROD-1119 Patients data layer).
/**
 * Scrips Billing API — TypeScript Contracts
 *
 * Source of truth: Flutter `scrips_msp1_flutter_shared/scrips_shared_features/lib/features/billing_module/data/datamodels/`
 *
 * Field names and JSON keys mirror the Flutter datamodels exactly so the
 * existing .NET backend endpoints work without translation. Where Flutter
 * has a typo on a class name (`CoporateAgreementModel`), we keep the wire
 * key but spell the TS interface correctly.
 *
 * NOTE: Cross-cutting types (PatientData, Code, BillingGroup, etc.) are
 * defined here as minimal placeholder shapes. They can be lifted to a
 * shared `shared/types/common.ts` file once a second domain (clinical,
 * patients) needs them. Each placeholder is labelled with its Flutter
 * source path for easy enrichment later.
 */

// ─── Cross-cutting placeholders (enrich as other domains need them) ──────────

/** Generic Coding shape — matches FHIR Coding minus version. */
export interface Code {
  system?: string;
  code: string;
  display: string;
}

/**
 * Procedure code shape — matches Flutter `code_display_name.dart`. The
 * wire field is `displayName` (Flutter exposes it as `displayN` internally
 * and via a getter `displayName`); we keep `displayName` so JSON
 * (de)serialization is straight-through.
 */
export interface CodeDisplayName {
  code?: string;
  displayName?: string;
  system?: string;
  description?: string;
  shortName?: string;
}

/**
 * Search response item from `GET /api/Master/ServiceCodesList?searchQuery=...`.
 * Mirrors Flutter `CodeList` (in speciality_valueset_list_model.dart:18).
 */
export interface ProcedureCodeListItem {
  specialitySkillId?: string;
  code?: CodeDisplayName;
}

/** Generic value-set entry used for filters (status, sponsor, etc.). */
export interface CodeList {
  code: string;
  display: string;
}

/** Patient summary shape used in invoices and claims. */
export interface PatientData {
  id?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  dateOfBirth?: string;
  gender?: Gender;
  imageUrl?: string;
  fileNumber?: string;
}

/** Provider summary shape used in invoices and claims. */
export interface ProviderData {
  id?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  imageUrl?: string;
  speciality?: string;
}

/** Practice summary shape used in invoices. */
export interface PracticeData {
  id?: string;
  name?: string;
  trnNumber?: string;
  address?: string;
  phoneNumber?: string;
  logoUrl?: string;
}

/** Organization summary shape attached to invoices. */
export interface OrganizationData {
  id?: string;
  userId?: string;
  name?: string;
  logoUrl?: string;
}

/**
 * Sex/gender code. Backend sends an object with `id` (canonical lower-case
 * code) and `name` (display label) — mirrors Flutter `Gender` model in
 * `scrips_shared_features/lib/features/common/data/datamodels/gender_model.dart`.
 */
export interface Gender {
  id?: string;
  name?: string;
}

/**
 * Practice location, as returned by `GET /api/Organization/{orgId}/Locations`
 * and embedded inside `VisitFee.location` on a service. Mirrors Flutter
 * `appt_locations_model.dart`.
 *
 * NOTE: there is NO `category` field on the wire — the table groups fees
 * by `location.name` (e.g. "Office", "Virtual", "Home"), since location
 * names are the practice's source of truth.
 */
export interface ApptLocations {
  id?: string;
  name?: string;
  imageUrl?: string;
  description?: string;
  isArchived?: boolean;
}

/**
 * Billing group classifier on a service. Mirrors Flutter
 * `billing_settings_model.dart` `BillingGroup`. `category` carries the
 * display string (e.g. "Drug", "Lab", "Consultation"); there is NO
 * `code` / `display` pair on the wire.
 */
export interface BillingGroup {
  id?: string;
  category?: string;
  system?: string;
  organizationId?: string;
}

// TpaModel canonical definition lives in the v2 block further down —
// search for "TpaContactDetails". The earlier scaffold here defined a
// narrower 3-field variant; removed to keep one source of truth.

/**
 * Per-line claim observation. Mirrors Flutter `ObservationsDataModel`
 * (`scrips_shared_features/.../billing_module/data/datamodels/observations_data_model.dart`)
 * and the swagger `ClaimObservationDto` (`http://localhost:6012/swagger`).
 *
 * `observationType` is a string enum — see `OBSERVATION_TYPE_*` in
 * `src/billing/lib/billing-constants.ts`. `observationCode`,
 * `observationValue`, and `observationValueType` are context-dependent
 * based on `observationType` — see CONVERSION_NOTES "Magic strings".
 *
 * Note the Dart→JSON casing quirk on `fileSize`: Flutter's Dart field
 * is `filesize` (lowercase 's') but the JSON key is `fileSize` (camelCase).
 * We follow the wire — `fileSize` here.
 */
export interface ObservationsDataModel {
  id?: string;
  claimId?: string;
  serviceId?: string;
  /** `'Text' | 'Result' | 'File' | 'UniversalDental'` — see OBSERVATION_TYPE_* constants. */
  observationType?: string;
  observationCode?: string | null;
  observationValue?: string | null;
  observationValueType?: string | null;
  fileName?: string | null;
  fileSize?: string | null;
  isActive?: boolean;
  createdOn?: string;
  createdById?: string;
  updatedOn?: string;
  updatedById?: string;
  isDeleted?: boolean;
}

/**
 * Resubmission type wire value. Note the display-vs-wire drift for
 * Internal Complaint: dropdown label is `'Internal Complaint'` (space),
 * wire is `'InternalComplaint'` (no space). See Flutter
 * `getResubmissionType()` in `review_claim_popup.dart:3231-3235`.
 */
export type ResubmissionType =
  | 'InternalComplaint'
  | 'Correction'
  | 'Reconciliation';

/**
 * Body for `PUT /api/Billing/InvoiceClaim/{claimId}/ResubmissionType`.
 *
 * Note: swagger schema also lists `claimId`, `userId`, `organizationId`
 * — Flutter omits them and works (backend reads `claimId` from path +
 * `userId`/`organizationId` from JWT). We mirror Flutter and omit.
 */
export interface UpdateResubmissionTypeRequest {
  resubmissionComment?: string | null;
  resubmissionType?: ResubmissionType | null;
  resubmissionAttachmentFileUrl?: string | null;
  resubmissionAttachmentFileName?: string | null;
}

/** Clinical assessment ref attached to invoices. Enrich when clinical lands. */
export interface PatientAssessment {
  id?: string;
}

/** Backend sponsor ref — slim shape used in claim workbench rows. */
export interface Sponsor {
  id?: string;
  name?: string;
  imageUrl?: string;
  type?: string;
}

// ─── Billing Settings ────────────────────────────────────────────────────────

/**
 * Currency block embedded in `OrgBillingSettings`. Mirrors Flutter
 * `billing_settings_model.dart` `Currency`.
 */
export interface Currency {
  code?: string;
  currencyName?: string;
  symbol?: string;
  country?: string;
}

/**
 * Organization-scoped billing settings. Returned by
 * `GET /api/Billing/Settings/{organizationId}` — same endpoint Flutter
 * hits in `settings_data_source_impl.dart:1276 fetchBillingSettings`.
 *
 * `billingGroups` is the source of truth for the Billing Group dropdown
 * on a service. Items carry their server `id` + `system` + `organizationId`
 * which the create/update payload needs to round-trip intact.
 */
export interface OrgBillingSettings {
  currency?: Currency;
  followUpValidity?: number;
  invoiceNumberFormat?: string;
  receiptNumberFormat?: string;
  billingGroups?: BillingGroup[];
}

// ─── Service List ────────────────────────────────────────────────────────────

export type ServiceListType = 'cash' | 'insurance' | 'agreement' | string;

export interface MasterServiceListModel {
  serviceListName?: string;
  serviceListId?: string;
  isDefault?: boolean;
  validityStartDate?: string;
  validityEndDate?: string;
  createdOn?: string;
  updatedOn?: string;
  isArchived?: boolean;
  servicesCount?: number;
  listType?: ServiceListType;
}

// ─── Billing Service (line item) ─────────────────────────────────────────────

export interface VisitFee {
  id?: string;
  location?: ApptLocations;
  isArchived?: boolean;
  fee?: number;
  serviceId?: string;
}

export interface BillingServiceModel {
  id?: string;
  serviceListId?: string;
  serviceListName?: string;
  procedureCode?: CodeDisplayName;
  shortDescription?: string;
  billingGroup?: BillingGroup;
  genders?: Gender[];
  visitFees?: VisitFee[];
  isArchived?: boolean;
  createdOn?: string;
  createdById?: string;
  updatedOn?: string;
  updatedById?: string;
  listType?: ServiceListType;
  /** Local UI flag — not on the wire. */
  isProviderFeeSearch?: boolean;
}

/** Server response from POST /ServiceLists/ValidateCsv. */
export interface ValidateCsvResponse {
  validCsv?: string;
  invalidCsv?: string;
  validRecords?: number;
  invalidRecords?: number;
  /** Map of CSV row identifier → array of error messages for that row. */
  invalidRecordsErrors?: Record<string, unknown[]>;
  /** Opaque token to pass to /ImportCsv to commit the validated payload. */
  requestId?: string;
  originalFileName?: string;
}

/**
 * Server response from POST /ServiceLists/ExportCsv. Also reused by
 * `/InvoiceClaim/Claims/Submission/Export{Csv,Xml}` and
 * `/InvoiceClaim/Claims/Resubmission/Export{Csv,Xml}` per the backend
 * OpenAPI (`ExportCsvDto`). The XML endpoints reuse the same envelope
 * — `csvLink` is the URL field even when the URL points at XML.
 *
 * `count` is populated by the Submission/Resubmission export endpoints
 * (rows in the bundle); legacy ServiceLists exports may omit it.
 */
export interface ExportCsvResponse {
  csvLink?: string;
  count?: number;
}

/** Paginated response shape for /Services queries. */
export interface BillingServiceListModel {
  pageNumber?: number;
  pageSize?: number;
  totalNumberOfPages?: number;
  totalNumberOfRecords?: number;
  results?: BillingServiceModel[];
}

// ─── Invoice ─────────────────────────────────────────────────────────────────

export interface DxPointer {
  invoiceId?: string;
  serviceId?: string;
  diagnosisId?: string;
  dxPointerCode?: Code;
  note?: string;
  isDeleted?: string;
}

export interface PatientInvoiceService {
  quantity?: number;
  id?: string;
  invoiceId?: string;
  serviceId?: string;
  serviceAddedDate?: string;
  servicePlaceById?: string;
  serviceIncluded?: boolean;
  grossAmount?: number;
  discountAmount?: number;
  netAmount?: number;
  sponsorAmount?: number;
  patientAmount?: number;
  amountToResubmit?: number;
  payment?: number;
  outstanding?: number;
  createdOn?: string;
  createdById?: string;
  updatedOn?: string;
  updatedById?: string;
  isArchived?: boolean;
  serviceExcluded?: boolean;
  serviceNeedsApproval?: boolean;
  dxPointers?: DxPointer[];
  procedureCode?: CodeDisplayName;
  serviceFee?: VisitFee;
  authCode?: string;
  denialReason?: string;
  denialCode?: string;
  validityStartDate?: string;
  validityEndDate?: string;
  billingGroupCategory?: string;
  billingGroupSystem?: string;
  discountReason?: string;
  observations?: ObservationsDataModel[];
  /** Local UI state — not on the wire. */
  isSelected?: boolean;
  /** Local UI state — not on the wire. */
  type?: string;
}

/** Diagnosis assessment row attached to an invoice. */
export interface AssessmentPlan {
  id?: string;
  diagnosisCode?: Code;
  note?: string;
  isPrimary?: boolean;
}

/** Server-side validation error attached to an invoice. */
export interface InvoiceError {
  header?: string;
  message?: string;
}

/** Nested claim block on an invoice — minimal shape; full Claim row lives in `ClaimWorkbenchRow`. */
export interface InvoiceClaim {
  id?: string;
  claimId?: string;
  claimStatus?: string;
  claimDate?: string;
  payerComment?: string;
}

export interface InvoiceMainModel {
  id?: string;
  invoiceNumber?: string;
  invoiceStatus?: string;
  policyNo?: string;
  sponsorType?: string;
  sponsor?: Sponsor;
  provider?: ProviderData;
  appointmentId?: string;
  encounterId?: string;
  locationId?: string;
  encounterNumber?: string;
  invoiceDate?: string;
  draftedOn?: string;
  encounterDate?: string;
  patient?: PatientData;
  practice?: PracticeData;
  patientId?: string;
  providerId?: string;
  practiceId?: string;
  claim?: InvoiceClaim;
  diagnosisList?: AssessmentPlan[];
  services?: PatientInvoiceService[];
  note?: string;
  grossTotal?: number;
  discountTotal?: number;
  netTotal?: number;
  patientPayable?: number;
  deposited?: number;
  sponsorOutstanding?: number;
  sponsorPaymentTotal?: number;
  patientOutstanding?: number;
  excludedTotal?: number;
  patientShareTotal?: number;
  sponsorAmountTotal?: number;
  patientPaymentStatus?: string;
  modified?: boolean;
  eligibilityEndDate?: string;
  eligibilityStartDate?: string;
  errors?: InvoiceError[];
  assessmentPlan?: PatientAssessment;
  acceptedRejectionAmount?: number;
  /** Wire JSON key: "organization" — Flutter aliases this as `organizationData`. */
  organizationData?: OrganizationData;
  resubmissionType?: string;
  resubmissionComment?: string;
  resubmissionAttachmentFileUrl?: string;
  resubmissionAttachmentFileName?: string;
  managementDiscountAmount?: number;
  trnNumber?: string;
  managementDiscount?: number;
  providerName?: string;
}

// ─── Claim Workbench ─────────────────────────────────────────────────────────
// Type definitions live in the v2 block further down — search for
// "Claim Workbench (Coding & Claims)". The earlier scaffold here defined
// `ClaimWorkbenchData` / `ClaimWorkbenchListModel` / `ClaimWorkbenchFacets`
// that were never wired; removed to keep one source of truth.

// ─── Cashier Shift ───────────────────────────────────────────────────────────
// The canonical CashierShiftModel + CashierShiftStatus definitions live
// in the v2 block further down — search for "Cashier Shift detail page".
// The earlier scaffold here defined narrower variants that conflicted
// with the wire shape (nullable shiftCloseDate, etc.); removed to keep
// one source of truth.

// ─── Payments ────────────────────────────────────────────────────────────────
//
// The canonical Payment-row shape is `PaymentRow` further down — search for
// "PaymentRow". An earlier `PaymentModel` stub here was added speculatively
// alongside PROD-694/695 and never consumed; removed 2026-05-20 once
// PaymentRow shipped with the full Flutter `payment_model.dart` field set.
// `PaymentMethod` was a sibling string-union that PaymentModel was its only
// consumer of — also removed.

// ─── Patient Billing (Tab 5 — PROD-733) ──────────────────────────────────────

/** Generic .NET `PagedResults<T>` envelope used by `/Billing/Payment/*` list
 *  endpoints. The Search endpoints inside `/Invoice/*` instead ship a flat
 *  array. */
export interface PagedResults<T> {
  pageNumber?: number;
  pageSize?: number;
  totalNumberOfPages?: number;
  totalNumberOfRecords?: number;
  results?: T[] | null;
}

/** One line of a patient's available-balance card. The backend buckets
 *  amounts by `subject` (e.g. "Cash", "Insurance"); each carries a
 *  `financialEventStatus` ("Available" / "On Hold" / etc.). */
export interface BalanceLine {
  subject?: string | null;
  amount?: number;
  financialEventStatus?: string | null;
}

export interface AvailableBalanceResponse {
  patientId?: string;
  balance?: BalanceLine[] | null;
}

/** Request body for `POST /Billing/Invoice/SearchInvoices/{patientId}`.
 *  Headers carry `userId` / `organizationId` — backend will accept the
 *  query-string equivalents too, but the api-client never sets those.
 *  Leave the matching body fields blank/null; the .NET filter prefers
 *  the header values. */
export interface GetInvoicesQuery {
  searchQuery?: string | null;
  isArchived?: boolean;
  pageSize: number;
  pageNumber: number;
  userId?: string;
  patientId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  sponsorType?: string[] | null;
  patientPaymentStatus?: string[] | null;
  query?: string | null;
  organizationId?: string;
}

/** Request body for `POST /Billing/Payment/AdvancePaymentReceipts?patientId=`. */
export interface AdvancePaymentsListRequest {
  query?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  receiptId?: string | null;
  paymentMethod?: string[] | null;
  pageNumber: number;
  pageSize: number;
}

/** Row in the Advance Receipts table. Trim is intentional — the wire ships
 *  ~25 fields but the list view renders ~7. The Cancel / Refund popups
 *  (PR C) will extend the shape when needed. */
export interface AdvancePaymentReceipt {
  id?: string;
  receiptNumber?: string | null;
  receiptDate?: string;
  receiptAmount?: number;
  availableBalance?: number;
  paymentDate?: string;
  paymentMethod?: string | null;
  status?: string | null;
  note?: string | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  invoiceId?: string | null;
  cashierShiftId?: string;
  voidedDate?: string | null;
  /** Lightweight slice of the (much wider) provider envelope. */
  provider?: {
    firstName?: string | null;
    lastName?: string | null;
    photoURL?: string | null;
  };
  /** Lightweight slice — fuller shape lives under `ExtendUserDto` on the wire. */
  receivedBy?: {
    firstName?: string | null;
    lastName?: string | null;
  };
}

// ─── Patient Billing — Advance Payment add + Refunds list (Tier-3) ───────────
//
// Source: Flutter `patient_billing_data_source_impl.dart:41, 95` +
// `advance_payment_request_model.dart`, `advance_payment_response.dart`,
// `patient_refund_list_request_model.dart`, `patient_refund_list_model.dart`.
//
// Sister endpoint `?PatientId=` AdvancePaymentReceipts is the existing
// `AdvancePaymentsListRequest` / `AdvancePaymentReceipt` above (this file
// only ADDS the advance-add + refunds-list types — does not touch them).

/** Body for `POST /api/Billing/Payment/Create/Patient/{patientId}`.
 *  Flutter `AdvancePaymentRequest` (5 fields). `receiptDate` ships as no-Z
 *  ISO (e.g. `2026-05-20T15:30:00.000`) to mirror the sibling `AddPaymentRequest`
 *  convention — same .NET binder, same date wire format. */
export interface AdvancePaymentRequest {
  paymentMethod?: string;
  paidAmount?: number;
  note?: string;
  /** Operator user id (auth context). */
  receivedBy?: string;
  /** No-Z ISO. */
  receiptDate?: string;
}

/** Response of `POST /api/Billing/Payment/Create/Patient/{patientId}`.
 *  Flutter `AdvancePaymentResponse`. */
export interface AdvancePaymentResponse {
  status?: string | null;
  errorDetails?: string | null;
  receiptId?: string | null;
}

/** Body for `POST /api/Billing/Payment/Refunds/Patient/{patientId}`.
 *  Flutter `AdvanceRefundListRequest`. `refundMethod` is array-of-string
 *  enum codes ("Cash" / "Card" / "Bank").
 *
 *  **Wire constraint (verified 2026-05-20 dev curl):** `refundMethod` MUST
 *  ship as `[]` (empty array) when no filter is applied — the backend
 *  500s on `refundMethod: null`. This deviates from Flutter
 *  `AdvanceRefundListRequest.toJson` (line 58) which ships `null` when
 *  null — but since the Flutter consumer doesn't exist, that ship-null
 *  path is never exercised. Live curl wins per CLAUDE.md precedence rule. */
export interface AdvanceRefundListRequest {
  query?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  /** Empty array `[]` for no filter; never `null` (backend 500s on null). */
  refundMethod: string[];
  pageNumber: number;
  pageSize: number;
}

/** Patient-scoped refund row — shape verified against the 2026-05-20 dev
 *  curl response. Different from the invoice-scoped `RefundPayment` rows
 *  in `use-payments.ts`.
 *
 *  **Flutter model drift**: Flutter `RefundList`
 *  (`patient_refund_list_model.dart:64`) documents `refundReason:
 *  CancellationReason {code, display, definition}` but the live wire
 *  ships `refundReason: {id, value}` — flatter shape. Live curl wins. */
export interface PatientRefundRow {
  id?: string | null;
  refundReceiptNumber?: string | null;
  linkedReceiptNumber?: string | null;
  refundReceiptDate?: string | null;
  amount?: number | null;
  refundDate?: string | null;
  refundMethod?: string | null;
  status?: string | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  encounterId?: string | null;
  encounterDate?: string | null;
  refundReason?: {
    id?: string | null;
    value?: string | null;
  } | null;
  provider?: {
    id?: string | null;
    name?: string | null;
    photo?: string | null;
    speciality?: {
      specialitySkillId?: string | null;
      code?: {
        code?: string | null;
        displayName?: string | null;
        system?: string | null;
      } | null;
    } | null;
    timeZone?: string | null;
    organizationId?: string | null;
  } | null;
  practice?: {
    id?: string | null;
    name?: string | null;
    phone?: string | null;
    address?: string | null;
    country?: string | null;
    city?: string | null;
    office?: string | null;
    photo?: string | null;
  } | null;
  refundedBy?: {
    id?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    middleName?: string | null;
    photo?: string | null;
    organisationId?: string | null;
  } | null;
  receivedBy?: {
    id?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  } | null;
  cashierShiftId?: string | null;
}

/** Response envelope of `POST /api/Billing/Payment/Refunds/Patient/{patientId}`.
 *
 *  **Wire correction (2026-05-20 dev curl):** the backend ships the
 *  generic `PagedResults<T>` envelope (`pageNumber / pageSize /
 *  totalNumberOfPages / totalNumberOfRecords / results`), NOT Flutter's
 *  documented `PatientRefundListModel` (`count / currentPage / totalPage
 *  / refundList`). Flutter's model file is stale; live curl wins per
 *  CLAUDE.md precedence rule. The Flutter type lives in
 *  `patient_refund_list_model.dart:17` — never deserialized in practice
 *  because no Flutter UI dispatches `FetchPatientRefundsEvent`. */
export type PatientRefundsListResponse = PagedResults<PatientRefundRow>;

// ─── Payers (Insurance / Corporate / Patient Package / TPA) ──────────────────
//
// Type definitions live in the v2 block further down (search for
// "Insurance Payers (Agreements / InsuranceAgreements)"). The earlier
// scaffold here defined parallel `*Model` interfaces (InsuranceAgreementModel,
// CorporateAgreementModel, PatientPackageModel) that were never wired —
// they have been removed to keep this file single-source for the wire
// shapes.

// The canonical TPA master shape is `TpaModel` further down — search for
// "TpaContactDetails" / "TpaModel". An earlier `ThirdPartyAdministratorModel`
// stub here was the speculative-9-field grandparent that never got
// consumed once TpaModel + TpaListResponse shipped with the verified
// wire shape; removed 2026-05-20.

// ─── Practitioner / Doctor list ──────────────────────────────────────────────
//
// Source: `GET /api/v1/Practice/{organizationId}/PractitionerRoles?role=doctor&showArchived=...`
// Flutter wire model: `doctors_list_model.dart` (`PractitionersList` / `DoctorList`).
// Used by the Provider Fees picker grid to render the cards of doctors.

/** Wire shape of `speciality` on a practitioner list item — the
 *  practitioner endpoint returns the rich coding (with displayName +
 *  description fields) wrapped one level deeper than the simpler
 *  `CodeList` used elsewhere. Keep this fixture-friendly. */
export interface PractitionerSpeciality {
  code?: {
    code?: string;
    display?: string;
    displayName?: string;
    description?: string;
  };
  display?: string;
}

export interface PractitionerListItem {
  organizationId?: string;
  doctorSetupId?: string;
  firstName?: string;
  lastName?: string;
  emailId?: string;
  isSuperAdmin?: boolean;
  invitationStatus?: boolean;
  isLastLogin?: string;
  /** Wire JSON key: "photoURL" — yes, capital URL. */
  photoURL?: string;
  speciality?: PractitionerSpeciality;
  doctorSpecialities?: PractitionerSpeciality[];
  doctorSpecialityIds?: string[];
}

export interface PractitionerListResponse {
  pageNumber?: number;
  pageSize?: number;
  totalNumberOfPages?: number;
  totalNumberOfRecords?: number;
  results?: PractitionerListItem[];
}

// ─── Provider Service Fees (per-provider × service overrides) ────────────────
//
// Source: `/api/Billing/ProviderServiceFees` (CRUD + batch archive/unarchive +
// CSV import). Flutter wire model: `provider_service_fee_model.dart`.

export interface ProviderFeePercent {
  cash?: number;
  insurance?: number;
  corporate?: number;
}

export interface ProviderFeeShare {
  id?: string;
  providerId?: string;
  location?: ApptLocations;
  /** When true, `percent` is used; when false, `fixed` is used. */
  isPercent?: boolean;
  percent?: ProviderFeePercent;
  fixed?: number;
  isArchived?: boolean;
  createdOn?: string;
  createdById?: string;
  updatedOn?: string;
  updatedById?: string;
}

export interface ProviderServiceFee {
  id?: string;
  providerId?: string;
  serviceId?: string;
  procedureCode?: CodeDisplayName;
  /** Withholding tax percentage, 0–100. */
  wTax?: number;
  /** Reference fees pulled from the service-list service, per location. */
  serviceVisitFees?: VisitFee[];
  /** Provider-specific override fees, per location. Seeded from service defaults on create. */
  providerVisitFees?: VisitFee[];
  /** Per-location fee-share configuration (percent or fixed). */
  providerFeeShares?: ProviderFeeShare[];
  isArchived?: boolean;
  shortDescription?: string;
}

export interface ProviderServiceFeeListResponse {
  pageNumber?: number;
  pageSize?: number;
  totalNumberOfPages?: number;
  totalNumberOfRecords?: number;
  results?: ProviderServiceFee[];
}

/** Request body for POST /ProviderServiceFees/ImportCsv/{providerId}. */
export interface ProviderServiceFeesImportCsvRequest {
  requestId: string;
  fileName: string;
  removeExisting: boolean;
}

// ─── Cashier Shift (api_constants.dart:296,503-504 — second .NET service) ────

/**
 * A cashier shift — open or closed. Wire shape verified 2026-05-12 against
 * `GET /api/CashierShift?...` on dev. Fields mirror Flutter
 * `cashier_shift_model.dart`.
 *
 * Note `credit` on the wire is rendered as "Card" in the UI (Flutter same).
 * `practice` is a GUID — resolve to a name through the list-response facets.
 */
export interface CashierShiftModel {
  id?: string;
  shiftNumber?: string;
  shiftOpenDate?: string;  // ISO 8601 without `Z` suffix
  shiftCloseDate?: string | null;
  user?: string;           // display name (already resolved on the wire)
  userImageUrl?: string | null;
  practice?: string;       // GUID
  status?: CashierShiftStatus;
  openingBalance?: number;
  closingBalance?: number | null;
  cash?: number;
  /** Wire field; UI label is "Card". */
  credit?: number;
  bank?: number;
  total?: number;
  /** Always "Onsite" in current data; reserve enum for future. */
  category?: string;
}

/** Closed enum derived from `status` field across all 137 dev shifts. */
export type CashierShiftStatus = 'Open' | 'Closed';

/** Server-side facet entries (filter dropdown values). */
export interface CashierShiftFilterEntry {
  id?: string;
  name?: string;
  imageUrl?: string | null;
}

/** `GET /api/CashierShift` response — wraps `results` + facets + pagination. */
export interface CashierShiftListResponse {
  facets?: {
    shiftUsers?: CashierShiftFilterEntry[];
    practices?: CashierShiftFilterEntry[];
  };
  pageNumber?: number;
  pageSize?: number;
  totalNumberOfPages?: number;
  totalNumberOfRecords?: number;
  results?: CashierShiftModel[];
}

/** Body for `POST /api/CashierShift/Open` — Flutter `open_cashier_shift_request_model.dart`. */
export interface OpenCashierShiftRequest {
  openingBalance: number;
  category: string;
}

/** Patient / doctor shape on a transaction row. */
export interface CashierShiftPartyRef {
  id?: string;
  name?: string | null;
  imageUrl?: string | null;
  mrn?: string;
}

/**
 * One row in `POST /api/Billing/Payment/PaymentReceipts/{shiftId}` results.
 *
 * Flutter `cashier_shift_trasnsaction_response_model.dart` calls the
 * void-reason field `voidReason`, but the wire actually returns
 * `refundReason` — Flutter's mapping reads the wrong key and is always null.
 * We use `refundReason` (the truth) on the React side.
 */
export interface CashierShiftTransaction {
  receiptNumber?: string;
  refundNumber?: string | null;
  encounterNumber?: string | null;
  invoiceNumber?: string | null;
  patient?: CashierShiftPartyRef;
  doctor?: CashierShiftPartyRef;
  cash?: number;
  /** Wire `card` (the transactions response uses `card`, not `credit` — note the inconsistency with the shift row). */
  card?: number;
  bank?: number;
  transactionType?: CashierShiftTransactionType;
  refundReason?: string | null;
  transactionDate?: string;
  currency?: string;
  location?: string;
  cashierShiftId?: string;
}

/** Closed enum derived from `transactionType` across the captured transactions. */
export type CashierShiftTransactionType = 'Paid' | 'Refunded';

/** Body for `POST /api/Billing/Payment/PaymentReceipts/{shiftId}`. */
export interface CashierShiftTransactionsRequest {
  doctors: string[];
  practices: string[];
  shiftDate: string | null;       // no-Z ISO when set
  transactionType: string[];
  paymentMode: string[];
}

/** Response wrapper for transaction list. */
export interface CashierShiftTransactionsResponse {
  facets?: {
    doctors?: CashierShiftFilterEntry[];
    practices?: CashierShiftFilterEntry[];
  } | null;
  pageNumber?: number;
  pageSize?: number;
  totalNumberOfPages?: number;
  totalNumberOfRecords?: number;
  results?: CashierShiftTransaction[] | null;
}

// `ExportCsvResponse` is declared once near the top of this file. Declaration
// interface-merges the `count?: number` field across both spots so cashier-shift
// + service-lists + claim-submission + claim-resubmission all share one shape.

// ─── Payments (Receive / Void / Refund) ──────────────────────────────────────

/**
 * Payment row — `GET /api/Billing/Payment/PaymentReceipts?invoiceId={id}`.
 * Mirrors Flutter `Payment` (data/datamodels/payment_model.dart).
 *
 * Each payment carries the GUID `id` (needed for void/refund), a receipt
 * number, the amount(s), and party refs. `status` is `'Received'` |
 * `'Voided'` | `'Refunded'` | `'Linked'`. `linkedReceiptId` ties refunds
 * back to the original payment.
 */
export interface PaymentRow {
  id?: string;
  receiptNumber?: string;
  receiptDate?: string;
  amount?: number;
  /**
   * The backend payload contains a TYPO — `recieptAmount` (transposed
   * "ei"). Mirrored here intentionally for wire parity; the Flutter
   * source preserves the same typo at `payment_model.dart:62`. DO NOT
   * "fix" this to `receiptAmount` — the mapping would break silently
   * because the .NET model binder is case + spelling sensitive on this
   * field. If the backend ever corrects the typo, add the corrected
   * key alongside (don't replace).
   */
  recieptAmount?: number;
  paymentDate?: string;
  /** `'Cash' | 'Card' | 'Bank' | 'Cheque' | string` — wire-strings. */
  paymentMethod?: string;
  /** For refund rows, the receiptId of the original payment that was refunded. */
  linkedReceiptId?: string | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  voidedDate?: string | null;
  status?: string;
  voidReason?: CancellationReasonRef | null;
  patient?: InvoicePartyRef;
  provider?: InvoicePartyRef;
  practice?: InvoicePartyRef;
  /**
   * The receipts endpoint ships NAME PARTS (`firstName` / `middleName` / `lastName`), not a composed `name`
   * (pm-react PROD-1798, verified against `GET /Billing/Payment/PaymentReceipts`). Compose via `personRefName()`.
   */
  receivedBy?: PaymentUserRef;
  voidedBy?: PaymentUserRef;
  cashierShiftId?: string | null;
  encounterId?: string | null;
  encounterDate?: string | null;
}

/**
 * A user reference on a payment receipt (`receivedBy` / `voidedBy`). Ported from dev-scrips-pm-react
 * src/shared/types/billing.ts (PROD-3312): the wire ships name parts, not a composed `name`.
 */
export interface PaymentUserRef {
  id?: string;
  firstName?: string;
  middleName?: string | null;
  lastName?: string;
  /** Rarely present — some endpoints compose it; fall back to it when set. */
  name?: string | null;
}

/** Compose a display name from a payment user ref (first + middle + last), falling back to a pre-composed `name`. */
export function personRefName(ref?: PaymentUserRef | null): string | undefined {
  if (!ref) return undefined;
  const composed = [ref.firstName, ref.middleName, ref.lastName]
    .map((p) => p?.trim())
    .filter(Boolean)
    .join(' ')
    .trim();
  return composed || ref.name?.trim() || undefined;
}

export interface CancellationReasonRef {
  id?: string;
  value?: string;
}

/**
 * Body for `POST /api/Billing/Payment/Create/Invoice/{invoiceId}`.
 * Flutter `AddPaymentRequest`. `receiptDate` ships as no-Z ISO (e.g.
 * `2026-05-14T00:00:00.000`). `isPaidFull` defaults true at the Flutter
 * client; let the caller set explicitly.
 */
export interface AddPaymentRequest {
  paymentMethod?: string;
  paidAmount?: number;
  isPaidFull?: boolean;
  /** Operator user id (auth context). */
  receivedBy?: string;
  note?: string;
  /** No-Z ISO. */
  receiptDate?: string;
}

/**
 * Body for `PUT /api/Billing/Payment/Void/{receiptId}?linkedReceiptId={id}`.
 * Flutter `VoidPaymentRequest`. Backend lookups the reason via `voidReason.id`.
 */
export interface VoidPaymentRequest {
  voidReason?: CancellationReasonRef;
  voidedById?: string;
}

/**
 * Body for `POST /api/Billing/Payment/Refund/{receiptId}`.
 * Flutter `RefundPaymentRequest`. `refundTo` defaults to `'Cash'`.
 */
export interface RefundPaymentRequest {
  refundAmount?: number;
  refundTo?: string;
  refundReason?: CancellationReasonRef;
  refundedById?: string;
}

// ─── Invoice Detail ───────────────────────────────────────────────────────────

/**
 * Single invoice detail — `GET /api/Billing/Invoice/{invoiceId}`.
 *
 * Mirrors Flutter `InvoiceMainModel`. Only fields the detail page renders
 * are typed here; the wire response carries ~60 fields total (audit
 * metadata, resubmission attachments, tenant scaffolding) that we
 * pass-through opaquely.
 *
 * Pricing breakdown comes through as a flat set of named totals:
 *   grossTotal       — sum of all services before discount
 *   discountTotal    — total discount applied
 *   netTotal         — grossTotal − discountTotal
 *   patientShareTotal / sponsorAmountTotal
 *                    — split of net between patient and sponsor
 *   patientPayable   — what the patient is expected to pay
 *   deposited        — payments collected so far
 *   patientOutstanding / sponsorOutstanding
 *                    — what's still owed by each side
 *   excludedTotal    — services excluded from coverage
 *   sponsorPaymentTotal — payments received from the sponsor
 */
export interface InvoiceServiceFee {
  id?: string;
  fee?: number;
  location?: { id?: string; name?: string };
}

export interface InvoiceService {
  id?: string;
  serviceId?: string;
  procedureCode?: CodeDisplayName;
  shortDescription?: string;
  billingGroup?: BillingGroup;
  visitFees?: InvoiceServiceFee[];
  /** Per-service rolled-up fees on the invoice line. */
  fee?: number;
  discount?: number;
  /**
   * The invoice-service wire uses `grossAmount` / `discountAmount` (verified
   * live 2026-06-04 on PreLoadAppointmentInvoice + the AddRemoveService
   * response); `fee` / `discount` are the older/aliased names and are often
   * absent on these rows. Read `grossAmount ?? fee` for Gross display.
   */
  grossAmount?: number;
  discountAmount?: number;
  /**
   * The reason saved with the line discount (one of `DISCOUNT_REASONS`). Shipped on the
   * invoice-service row by `GET /Billing/Invoice/{id}` (pm-react verified it live 2026-07-24;
   * its edit-discount dialog seeds the picker from it on reopen). Mutated via
   * `UpdateServiceDiscount`'s `discountReason` body field.
   */
  discountReason?: string | null;
  netAmount?: number;
  patientShare?: number;
  /**
   * Per-line patient amount — the wire ships this in addition to
   * `patientShare` (verified live 2026-05-21 on `GET /Billing/Invoice/{id}`).
   * Same number on a paid invoice; prefer this field for the
   * PATIENT PAYABLE column display since `patientShare` is occasionally
   * null on draft rows.
   */
  patientAmount?: number;
  sponsorAmount?: number;
  isExcluded?: boolean;
  isApproval?: boolean;
  authorizationCode?: string;
  /**
   * The invoice-GET wire field for the service's authorization code
   * (`GET /Billing/Invoice/{id}` ships `authCode`, distinct from the
   * `authorizationCode` alias above). Forwarded verbatim in the Mark
   * Reviewed `services[].authCode` payload (Flutter `review_claim_popup.dart:3055`).
   */
  authCode?: string | null;
  /**
   * Per-service quantity. Edited via `useAddRemoveService()` PUT body's
   * `updateServiceQuantity[]` shape (PR #326).
   */
  quantity?: number;
  /** Service-added timestamp — no-Z ISO. */
  serviceAddedDate?: string | null;
  /**
   * Service validity window — the Wrap-Up invoice table's VALIDITY PERIOD column on a
   * sponsored invoice, and the `updateValidity[]` edit payload (Flutter
   * wrap_up_billing_screen.dart:925-939, where the end defaults to the start).
   *
   * VERIFIED against the backend 2026-08-06: `Scrips.Billing`
   * `src/Billing.Application/Features/Invoice/DTO/InvoiceDetailsDto.cs:23-24` declares both as
   * `DateTime?` on the invoice-GET service-line DTO. (Previously carried a "not live-verified"
   * caveat, added from Flutter's `PatientInvoiceService` alone — now confirmed at the source.)
   * Still optional: a line with no window set returns null and the column renders "—".
   */
  validityStartDate?: string | null;
  validityEndDate?: string | null;
  note?: string;
  /**
   * Existing observations attached to this service line. Populated server-side
   * on invoice fetch. Read-only in Phase 1; mutated via `/ClaimObservation*`
   * endpoints in Phase 2 (PROD-Claim-Review-Phase-2).
   */
  observations?: ObservationsDataModel[];
  denialReason?: string | null;
  denialCode?: string | null;
  /**
   * Order to Invoice: `"Manual"` when the line was added from a pending order, else null
   * (Billing `InvoiceDetailsDto.cs`: BillableSource / SourceOrderKey / AddedByName). With
   * `sourceOrderKey` set, the line can go back to Pending through `Orders/revert`.
   */
  billableSource?: string | null;
  /** The order the line came from, e.g. `v1:lab:80053`. */
  sourceOrderKey?: string | null;
  addedByName?: string | null;
  // ---- added from pm-react (PROD-3312) ----
  /** Flat coding-system + category as shipped on the invoice/claim service wire
   *  (e.g. "CPT" / "Procedure"). The nested `billingGroup` is NOT populated on
   *  the invoice-detail/claim-review payloads, so the line coding read these
   *  flat fields (PROD-1757). */
  billingGroupSystem?: string;
  billingGroupCategory?: string;
  /**
   * True when this line was charged at the patient's membership member price
   * rather than the location fee (PROD-2152). Recorded on the line by Billing
   * at pricing time, not derived on read: the plan can be repriced and the
   * membership can lapse, and neither may change what an invoice already
   * charged.
   *
   * Absent on every line written before the Billing column shipped, and on
   * every payer invoice — member pricing is self-pay only.
   */
  memberPriceApplied?: boolean;
  /**
   * The location fee this line carried before the member price replaced it.
   * Null/absent unless `memberPriceApplied`. The difference against the
   * charged amount is what the membership saved on this line.
   */
  listFeeBeforeMemberPrice?: number | null;
  /**
   * WHICH member-pricing rung set this line's fee (PROD-2525). Billing writes
   * it only when the plan's blanket `DiscountPercent` priced the line (rung 3),
   * holding the percentage actually used — `10` for a 10% plan. Null/absent
   * when an explicit per-service member price set it (rung 2), when no
   * membership moved it, and on every line priced before the column shipped
   * (no backfill), so null on a member-priced line does NOT prove rung 2 for an
   * old invoice. A snapshot taken at pricing time, never the patient's current
   * plan.
   */
  memberDiscountPercentApplied?: number | null;
  /**
   * Coverage flags — the ACTUAL wire keys on `GET /Billing/Invoice/{id}`
   * (Flutter `PatientInvoiceService` @ invoice_main_model.dart:1069-1081).
   * `serviceIncluded` / `serviceExcluded` / `serviceNeedsApproval` are the
   * live JSON keys; the coverage badge derives from them via Flutter's
   * `selectedCoverageOption()` (Included if serviceIncluded, else Needs
   * Approval if serviceNeedsApproval, else Excluded). See `inferCoverage`
   * in `claim-review/services-table.tsx`.
   */
  serviceIncluded?: boolean;
  serviceExcluded?: boolean;
  serviceNeedsApproval?: boolean;
  /** Sponsor payment received against this line (denial-mgmt column). */
  payment?: number;
  /** Amount flagged for resubmission on this line (denial-mgmt column). */
  amountToResubmit?: number;
}

export interface InvoiceDiscountLine {
  id?: string;
  discountAmount?: number;
  discountReason?: string;
  invoiceDetailsId?: string | null;
  appliesToServiceId?: string | null;
  createdOn?: string;
}

export interface InvoicePartyRef {
  id?: string;
  name?: string;
  imageUrl?: string | null;
  mrn?: string;
  specialty?: string;
}

/**
 * Sponsor reference on `InvoiceDetail` — richer than `InvoicePartyRef` because
 * the wire hydrates this slot with the full `PayerDetails` shape (the .NET
 * model binder joins the related Payer record). Consumers (Claim Review popup,
 * Sponsor section, Info grid) read `payerName`, `phoneNumber`, `address`, etc.
 * directly off this field — no extra GET against `/Payer/{id}` required.
 *
 * Verified live 2026-05-21 against the dev backend.
 */
export interface InvoiceSponsorRef extends InvoicePartyRef {
  payerName?: string;
  payerCompanyId?: string;
  payerLogoUrl?: string | null;
  address?: string;
  website?: string;
  email?: string;
  phoneNumber?: string;
  office?: string;
  note?: string;
}

/**
 * Patient reference on `InvoiceDetail` — richer than `InvoicePartyRef`. The
 * `GET /Billing/Invoice/{id}` wire hydrates the full patient record here
 * (age, gender, address, phone) — verified live 2026-06-02/03. So the Claim
 * Review info-grid reads Age/Gender/Address/Phone straight off this field; no
 * separate `GET /PatientModule/Patient/{id}` is needed (Flutter fetches it
 * separately, but the invoice response already carries it).
 */
export interface InvoiceDetailPatient extends InvoicePartyRef {
  age?: string;
  /** ISO DOB — rendered in the Clinical Note header (PROD-969c phase 3). */
  birthDate?: string;
  genderName?: string;
  gender?: string;
  phoneNumber?: string;
  email?: string;
  address?: string;
  building?: string;
  apartment?: string;
  city?: string;
  country?: string;
  fullAddress?: string;
}

/**
 * Diagnosis row on `InvoiceDetail.assessmentPlan.assessmentPlanCondition[]`.
 * The wire shape is richer than this (identifier, encounter, parentId,
 * childMedicalCode, externalCode, assessmentPlanId, subject, performer,
 * notes, onSetDate, endDate, …); only the fields the Claim Review popup
 * surfaces are typed here. Widen as later PRs surface more.
 *
 * **Field-name divergence:** the wire ships the diagnosis display text
 * under `display` here (NOT `displayName` as procedure codes do — verified
 * live 2026-05-21). So this uses an inline `code/system/display` shape
 * rather than `CodeDisplayName`. Don't "fix" it by switching.
 */
export interface InvoiceAssessmentPlanCondition {
  conditionCode?: {
    code?: string;
    /** Diagnosis text. Wire field is `display`, NOT `displayName`. */
    display?: string;
    system?: string;
  };
  isPrimary?: boolean;
  isDeleted?: boolean;
  onSetDate?: string | null;
  endDate?: string | null;
}

export interface InvoiceAssessmentPlan {
  identifier?: string;
  assessmentPlanCondition?: InvoiceAssessmentPlanCondition[];
}

export interface InvoiceNestedClaim {
  id?: string;
  claimNumber?: string;
  claimStatus?: string;
  paymentStatus?: string;
  submissionDate?: string | null;
}

export interface InvoiceErrorRow {
  /** Free-text validation / pricing error from the .NET model. */
  message?: string;
  code?: string;
  field?: string;
}

export interface InvoiceDetail {
  id?: string;
  invoiceNumber?: string;
  /**
   * Wire enum (Flutter `InvoiceMainModel.invoiceStatus`). Observed
   * values: `'Issued'`, `'Draft'`, `'Cancelled'`, `'Submitted'`.
   */
  invoiceStatus?: string;
  cancelledReason?: string | null;
  invoiceDate?: string;
  draftedOn?: string | null;
  encounterDate?: string | null;
  encounterNumber?: string | null;
  note?: string | null;
  policyNo?: string | null;
  trnNumber?: string | null;

  patientId?: string;
  providerId?: string;
  practiceId?: string;
  organizationId?: string;
  sponsorId?: string;
  appointmentId?: string;
  encounterId?: string;
  billingProfileId?: string;
  locationId?: string;

  providerName?: string;
  providerImageUrl?: string | null;
  practiceName?: string;
  practiceImageUrl?: string | null;

  // Party refs (richer than the flat *Name fields above)
  patient?: InvoiceDetailPatient;
  provider?: InvoicePartyRef;
  practice?: InvoicePartyRef;
  /** Sponsor carries the full PayerDetails shape on the wire — see `InvoiceSponsorRef`. */
  sponsor?: InvoiceSponsorRef;
  organization?: InvoicePartyRef;

  // Pricing breakdown
  grossTotal?: number;
  discountTotal?: number;
  netTotal?: number;
  patientShareTotal?: number;
  sponsorAmountTotal?: number;
  excludedTotal?: number;
  patientPayable?: number;
  deposited?: number;
  patientOutstanding?: number;
  sponsorOutstanding?: number;
  sponsorPaymentTotal?: number;
  managementDiscount?: number | null;

  patientPaymentStatus?: string;
  sponsorType?: string;
  sponsorDetails?: unknown;

  eligibilityStartDate?: string | null;
  eligibilityEndDate?: string | null;

  claim?: InvoiceNestedClaim | null;
  services?: InvoiceService[];
  discounts?: InvoiceDiscountLine[];
  errors?: InvoiceErrorRow[];
  /** Encounter assessment plan — source of the Diagnosis section rows. */
  assessmentPlan?: InvoiceAssessmentPlan | null;
  /**
   * Wire field `diagnosisList` ships as an AssessmentPlan-shaped array
   * carrying nested medications / procedures — NOT a flat diagnosis array.
   * The Claim Review popup reads `assessmentPlan.assessmentPlanCondition[]`
   * instead. Typed as `unknown` to discourage accidental reads.
   */
  diagnosisList?: unknown;

  // Resubmission fields — populated only on resubmitted invoices
  resubmissionType?: string | null;
  resubmissionComment?: string | null;
  resubmissionAttachmentFileUrl?: string | null;
  resubmissionAttachmentFileName?: string | null;

  // Audit metadata
  createdOn?: string;
  createdById?: string;
  updatedOn?: string;
  updatedById?: string;
  isArchived?: boolean;
}

// ─── Claim Workbench (Coding & Claims) ────────────────────────────────────────

/**
 * Server-side filter body for `POST /api/Billing/InvoiceClaim/Claims`.
 * Mirrors Flutter `ClaimWorkbenchListRequestModel`. All arrays are filter
 * facets — leave a field undefined (omit it from the body) to skip that
 * dimension. `startDate` / `endDate` are no-Z ISO strings when set.
 *
 * Pagination + free-text search ride as query params, not in this body:
 *   `?q={query}&pageNumber={n}&pageSize={n}&showArchived={bool}`
 */
export interface ClaimWorkbenchRequest {
  startDate?: string;
  endDate?: string;
  /** e.g. `'Submitted'`, `'Hold'`, `'Reviewed'`. Facet `claimStatuses[].name`. */
  claimStatus?: string[];
  /** Sponsor IDs (GUIDs). Facet `sponsors[].id`. */
  sponsors?: string[];
  /** TPA IDs (GUIDs). Facet `tpa[].id`. */
  tpa?: string[];
  /** e.g. `'Not Paid'`, `'Paid'`, `'Partially Paid'`, `'Fully Rejected'`. */
  sponsorPaymentStatus?: string[];
  /** Numeric strings — "3", "7", "15", "30", etc. (days since last resubmission). */
  lastResubmissionDate?: string[];
}

/** Lightweight party reference on workbench rows. Shared across patient / provider / sponsor / reviewer cells. */
export interface ClaimPartyRef {
  id?: string;
  name?: string;
  imageUrl?: string | null;
  /** Patient rows carry `mrn`; provider rows carry `specialty`. */
  mrn?: string;
  specialty?: string;
}

/** Slim TPA reference on a workbench row. */
export interface ClaimTpaRef {
  id?: string;
  name?: string;
  imageUrl?: string | null;
}

/** Invoice summary embedded in a workbench row — slim subset of `InvoiceMainModel`. */
export interface ClaimRowInvoice {
  id?: string;
  invoiceNumber?: string;
  encounterDate?: string;
  sponsorAmount?: number;
  patientShare?: number;
  outstandingSponsor?: number;
  paidAmount?: number;
  outstandingAmount?: number;
}

/** Encounter summary embedded in a workbench row. */
export interface ClaimRowEncounter {
  id?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * One row in the Claim Workbench list. Mirrors Flutter
 * `ClaimWorkbenchData` (the active class, not the commented-out variant
 * earlier in the same file).
 *
 * The wire `id` field is the **claim** id (not the invoice id). Drill
 * into `row.invoice.id` for navigation to the invoice detail screen.
 */
export interface ClaimWorkbenchRow {
  id?: string;
  /**
   * Claim primary key. The list endpoints (`/ClaimWorkbench`) ship `id`; the
   * batch drill-ins (`/Submission/Details`, `/Resubmission/Details`) ship
   * `claimId` for the same value. Detail rows prefer `claimId ?? id`.
   */
  claimId?: string;
  claimNumber?: string;
  claimStatus?: string;
  paymentStatus?: string;
  submissionDate?: string | null;
  lastResubmissionDate?: string | null;
  practiceId?: string;
  invoice?: ClaimRowInvoice;
  encounter?: ClaimRowEncounter;
  reviewer?: ClaimPartyRef;
  patient?: ClaimPartyRef;
  provider?: ClaimPartyRef;
  sponsor?: ClaimPartyRef;
  tpa?: ClaimTpaRef;
}

/** Per-dimension facet entry (id is usually null — filter via `name`). */
export interface ClaimWorkbenchFacetEntry {
  id?: string | null;
  name?: string;
  imageUrl?: string | null;
  count?: number;
}

export interface ClaimWorkbenchFacets {
  claimStatuses?: ClaimWorkbenchFacetEntry[];
  paymentStatuses?: ClaimWorkbenchFacetEntry[];
  lastResubmissionDates?: ClaimWorkbenchFacetEntry[];
  sponsors?: ClaimWorkbenchFacetEntry[];
  tpa?: ClaimWorkbenchFacetEntry[];
}

/** Full response shape from `POST /api/Billing/InvoiceClaim/Claims`. */
export interface ClaimWorkbenchListResponse {
  facets?: ClaimWorkbenchFacets;
  pageNumber?: number;
  pageSize?: number;
  totalNumberOfPages?: number;
  totalNumberOfRecords?: number;
  results?: ClaimWorkbenchRow[];
}

/**
 * Body for `PUT /api/Billing/InvoiceClaim/{claimId}/{claimStatus}` —
 * change-status action (Submit / Revise / MarkDenialAddressed / Hold /
 * Review / etc.). Mirrors Flutter `ClaimActionsRequest`.
 *
 * The action verb is encoded as a URL path segment, not the body. Body
 * carries the action context (per-service auth codes, revised policy
 * number, free-text note, whether a revision occurred).
 */
export interface ClaimActionsRequest {
  services?: Array<{
    id?: string;
    dateTime?: string;
    authCode?: string | null;
  }>;
  policyNumber?: string | null;
  note?: string | null;
  hasRevised?: boolean | null;
}

/**
 * Body for `PUT /Billing/InvoiceClaim/{claimId}/DenialAddressed`. Captured
 * 2026-06-02 — see `docs/CONVERSIONS/claim-review-popup/CONVERSION_NOTES.md`.
 * `resubmissionComment` is required (client guard); `resubmissionType` ships
 * the enum CODE (e.g. `InternalComplaint`), not the display label.
 */
export interface DenialAddressedRequest {
  policyNumber?: string | null;
  note?: string | null;
  resubmissionComment?: string | null;
  resubmissionType?: string | null;
}

/**
 * Body for `PUT /Billing/InvoiceClaim/{claimId}/Reissue` (Issue & Allocate).
 * Returns a bare boolean. Field is `policyNo` here (not `policyNumber`).
 */
export interface IssueAndAllocateRequest {
  patientId?: string;
  sponsorId?: string;
  policyNo?: string | null;
  note?: string | null;
  eligibilityStartDate?: string | null;
  eligibilityEndDate?: string | null;
  insuranceFrontPhoto?: string | null;
  insuranceBackPhoto?: string | null;
  copaymentType?: string | null;
  billingGroups?: unknown;
  hasRevised?: boolean | null;
}

/** `GET /Billing/Invoice/GetInvoiceMasterData?type=Cancellation` row. */
export interface CancellationReason {
  id?: string;
  value?: string;
}

// ─── Claims for Submission (PROD-699) ────────────────────────────────────────
//
// Surface: `/billing/claim-workbench/submission` — paginated list of DHPO
// submission batches plus per-row actions (Submit / Export CSV / Export XML /
// View Details / Errors). Mirrors Flutter `claims_for_submission_widget.dart`
// + `claim_submission_detail_screen.dart`. Live status updates arrive via the
// existing Billing DHPO SignalR hub (see `billing/realtime/use-billing-realtime.ts`).

/**
 * Body for `POST /api/Billing/InvoiceClaim/Claims/Submission`.
 *
 * Note the array shape on `batchStatus` + `sponsors` — different to
 * `ClaimSubmitRequest` (singular `batchStatus` + `sponsor`) and to
 * `ClaimSubmissionExportRequest` (also singular). The list endpoint uses
 * arrays, every other endpoint on this surface uses singulars.
 *
 * Default rollup query (3 status segments, all sponsors, last 30 days):
 *   { startDate, endDate, batchStatus: ['Submitted', 'Ready for Submission', 'Sending'], sponsors: [] }
 */
export interface ClaimSubmissionListRequest {
  /** No-Z ISO. Defaults to 30 days back from today. */
  startDate?: string;
  /** No-Z ISO. Defaults to today. */
  endDate?: string;
  batchStatus?: string[];
  /** Sponsor GUIDs. Empty = all. */
  sponsors?: string[];
}

/**
 * Rollup totals card on the list response. Note the typo `isurance` —
 * preserved verbatim from the .NET wire shape. Do NOT spell-correct;
 * the .NET model binder is spelling-sensitive.
 */
export interface ClaimSubmissionRollupTotals {
  value?: number;
  /** Wire JSON key — backend typo for `insurance`. Don't fix. */
  isurance?: number;
  corporate?: number;
}

/** Sponsor / status entry on the list facets. */
export interface ClaimSubmissionFacetEntry {
  id?: string | null;
  name?: string;
  imageUrl?: string | null;
}

export interface ClaimSubmissionFacets {
  sponsors?: ClaimSubmissionFacetEntry[];
  batchStatuses?: ClaimSubmissionFacetEntry[];
}

/**
 * One batch row in the Claims for Submission list. The row's primary key
 * is `claimRequestId` — SignalR updates use this to patch in place.
 *
 * `batchStatus` values seen on dev: `'Submitted'`, `'Ready for Submission'`,
 * `'Sending'`, `'Error'`, `'Connection Error'`. The 3-dot menu on a row is
 * gated by status — Submit is shown only on Ready / Error / Connection Error.
 */
export interface ClaimSubmissionBatchRow {
  claimRequestId?: string;
  batchId?: string;
  submitDate?: string;
  batchStatus?: string;
  sponsor?: ClaimSubmissionFacetEntry;
  practiceId?: string;
  totalClaims?: number;
  totalClaimed?: number;
  errorMessage?: string;
}

/** Response from `POST /api/Billing/InvoiceClaim/Claims/Submission`. */
export interface ClaimSubmissionListResponse {
  totalClaimed?: ClaimSubmissionRollupTotals;
  totalClaimsReadyForSubmission?: ClaimSubmissionRollupTotals;
  facets?: ClaimSubmissionFacets;
  pageNumber?: number;
  pageSize?: number;
  totalNumberOfPages?: number;
  totalNumberOfRecords?: number;
  results?: ClaimSubmissionBatchRow[];
}

/**
 * Body for `POST /api/Billing/InvoiceClaim/Claims/Submission/Submit`.
 *
 * `sponsor` is SINGULAR (not the array `sponsors` used on the list endpoint).
 * `claimRequestId` is nullable — null = submit all ready batches in the
 * date range for `sponsor` + `practiceId`; GUID = submit one specific batch.
 *
 * The known dev placeholder `00000000-0000-0000-0011-000000000009`
 * appears in working curls. Source: backend-defined sentinel (not in
 * Flutter constants). Pass it through when the caller has nothing more
 * specific — flagged as an open unknown in CONVERSION_NOTES.
 */
export interface ClaimSubmitRequest {
  startDate?: string;
  endDate?: string;
  sponsor?: string;
  claimRequestId?: string | null;
  practiceId?: string;
}

/** Response from Submit. Both fields nullable. */
export interface ClaimSubmitResponse {
  status?: string | null;
  errorMessage?: string | null;
}

/**
 * Body for `POST /api/Billing/InvoiceClaim/Claims/Submission/ExportCsv`
 * and `…/ExportXml`.
 *
 * Note `batchStatus` + `sponsor` are SINGULAR here (the list endpoint uses
 * arrays). Backend wire shape, not negotiable.
 */
export interface ClaimSubmissionExportRequest {
  startDate?: string;
  endDate?: string;
  batchStatus?: string;
  sponsor?: string;
  claimRequestId?: string;
  practiceId?: string;
}

/**
 * Body for `POST /api/Billing/InvoiceClaim/Claims/Submission/Details`.
 *
 * Scoped to one batch via `claimRequestId`. The other fields are passed
 * through but the backend treats `claimRequestId` as the primary key.
 * Response envelope is the same as `ClaimWorkbenchListResponse` — drill-in
 * rows are individual claims.
 */
export interface ClaimSubmissionDetailsRequest {
  startDate?: string | null;
  endDate?: string | null;
  batchStatus?: string[];
  sponsors?: string[];
  claimRequestId?: string;
  practiceId?: string;
}

/**
 * Body for `POST /api/Billing/InvoiceClaim/Claims/Submission/Errors/`.
 *
 * Note: path has a trailing slash; the field is `searchQuery` (NOT `q`
 * like the list endpoint). Singular `sponsor` and `status`.
 */
export interface ClaimSubmissionErrorRequest {
  sponsor?: string;
  searchQuery?: string;
  status?: string;
  claimRequestId?: string;
}

/** One row in the errors response. */
export interface ClaimSubmissionErrorRow {
  invoice?: { id?: string; invoiceNumber?: string };
  claim?: {
    id?: string;
    claimId?: string;
    claimDate?: string;
    originAmount?: number;
  };
  errorDetails?: string;
}

/** Response from the Errors endpoint. */
export interface ClaimSubmissionErrorListResponse {
  pageNumber?: number;
  pageSize?: number;
  totalNumberOfPages?: number;
  totalNumberOfRecords?: number;
  results?: ClaimSubmissionErrorRow[];
}

/**
 * Body for `POST /api/Billing/InvoiceClaim/Claims/Submission/Errors/File`.
 *
 * Most fields nullable when scoped by `claimRequestId`. Returns
 * `{ csvLink }` matching the export pattern.
 */
export interface ClaimSubmissionErrorFileRequest {
  startDate?: string | null;
  endDate?: string | null;
  batchStatus?: string | null;
  sponsor?: string | null;
  claimRequestId?: string;
  practiceId?: string | null;
}

// ─── Claims for Resubmission (PROD-700) ──────────────────────────────────────
//
// Surface: `/billing/claims-for-resubmission`. Mirrors PROD-699 structurally
// but operates on the Resubmission endpoint family. Backend bodies + response
// shapes are nearly-identical to Submission (per the local Billing OpenAPI at
// `/swagger/v1/swagger.json`); the few deltas are flagged in CONVERSION_NOTES.

/**
 * Body for `POST /api/Billing/InvoiceClaim/Claims/Resubmission`.
 * Backend type: `InvoiceClaimRequestFilterDto`.
 *
 * Extends `ClaimSubmissionListRequest` (Submission List) with `claimRequestId`
 * and `practiceId` — present on the wire schema but Flutter never sets them
 * on the List call. They're populated only on the Details endpoint, which
 * shares the same Dto. We keep them nullable here so the same TS type
 * round-trips through both List and Details without a second declaration.
 */
export interface ClaimResubmissionListRequest {
  /** No-Z ISO. Defaults to 30 days back from today. */
  startDate?: string;
  /** No-Z ISO. Defaults to today. */
  endDate?: string;
  batchStatus?: string[];
  sponsors?: string[];
  /** Always omitted on List; populated only on Details. */
  claimRequestId?: string | null;
  practiceId?: string | null;
}

/**
 * Body for `POST /api/Billing/InvoiceClaim/Claims/Submission/Resubmit`.
 * Backend type: `SubmitClaimsFilterDto` (the same Dto Submit uses).
 *
 * Note the endpoint path lives under `…/Submission/Resubmit`, NOT
 * `…/Resubmission/Submit`. Easy to get backwards.
 *
 * Flutter ALWAYS sets `claimRequestId` on resubmit (unlike Submit which
 * sends null when bundling all ready batches). One row = one resubmit batch.
 * `claimRequestId` is therefore required (not optional) in the TS contract
 * so the compiler enforces the invariant — Samer review on PR #145.
 */
export interface ClaimResubmitRequest {
  startDate?: string;
  endDate?: string;
  /** Singular GUID — Flutter populates this. The Dto also has a `sponsors[]` slot but Flutter doesn't use it. */
  sponsor?: string;
  /**
   * REQUIRED on resubmit (Flutter parity at
   * `claim_resubmission_screen.dart:629-640` — always carries the batch's
   * claimRequestId). The List endpoint always populates this on every row
   * in its response, so by construction the caller can never lack one.
   */
  claimRequestId: string;
  practiceId?: string;
}

// Response shape `ClaimSubmitResponse` from PROD-699 is reused unchanged
// (backend `BaseResponse` = `{ status, errorMessage }`).

/**
 * Body for `POST /api/Billing/InvoiceClaim/Claims/Resubmission/Details`.
 * Backend type: `SubmissionDetailRequestDto` — same shape as Submission/Details.
 */
export type ClaimResubmissionDetailsRequest = ClaimSubmissionDetailsRequest;

/**
 * Body for `POST /api/Billing/InvoiceClaim/Claims/Resubmission/ExportCsv`
 * and `…/ExportXml`. Backend type: `ExportCsvFilter` — same shape as
 * Submission's `ClaimSubmissionExportRequest`.
 */
export type ClaimResubmissionExportRequest = ClaimSubmissionExportRequest;

// Row shape on the Resubmission List is the same `ClaimSubmissionBatchRow`
// (backend `ClaimSubmissionDto`). Rollup tiles, facet entries, errors list
// envelope, and SignalR payload all reuse the PROD-699 types verbatim.

// ─── Third-Party Administrators (TPA) ────────────────────────────────────────

/**
 * TPA — claims handler between a provider and an insurance payer.
 *
 * Wire shape mirrors Flutter `tpa_model.dart`. `isCustom` distinguishes
 * org-defined TPAs (editable) from system master-data TPAs (read-only,
 * no 3-dot menu).
 */
export interface TpaContactDetails {
  phoneNumber?: string;
  website?: string;
  email?: string;
  address?: string;
  office?: string;
}

export interface TpaModel {
  id?: string;
  code?: string;
  companyName?: string;
  contactDetails?: TpaContactDetails;
  note?: string;
  imageUrl?: string | null;
  /** Org-defined (true) vs system master-data (false). Only `true` rows show the 3-dot menu. */
  isCustom?: boolean;
  isArchived?: boolean;
  createdById?: string;
  updatedById?: string;
  createdOn?: string;
  updatedOn?: string;
}

/** Response shape for `GET /api/Billing/ThirdPartyAdministrators`. */
export interface TpaListResponse {
  pageNumber?: number;
  pageSize?: number;
  totalNumberOfPages?: number;
  totalNumberOfRecords?: number;
  results?: TpaModel[];
}

// ─── Insurance Payers (Agreements / InsuranceAgreements) ─────────────────────

/**
 * Payer (insurance agreement). Mirrors Flutter `insurance_agreement_model.dart`.
 *
 * Wire shape verified 2026-05-13 against
 * `GET /api/Billing/Agreements/GetAll?type=Insurance`. 47 records on dev
 * with realistic data (some with `excludedServiceIds` populated, all
 * with linked `serviceListId`/`serviceListName`).
 *
 * Field naming notes:
 *  - `agreementType` is `'Insurance' | 'Corporate' | 'Patient'`
 *    (the wire string). Drives which sub-tab the payer shows under.
 *  - `payerDetails.payerCompanyId` is the INS-prefixed company code
 *    (e.g. `INS010`). Distinct from `id` which is a GUID.
 *  - `payerDetails.payerAgreementId` is the dotted `INS010_GN` code —
 *    not the same as `agreementDetails.agreementId` (which may be a
 *    free-text override).
 *  - `relatedTpa` is an array of `TpaModel` — links a payer to one or
 *    more TPAs. Most payers on dev have null here; the few that don't
 *    link to a single TPA in practice.
 *  - `excludedServiceIds` / `approvalServiceIds` are coverage rules —
 *    services NOT covered, or covered with pre-approval. Per-service
 *    fee overrides go in `agreementServiceFees`.
 *  - `billingGroupPercentage[]` populates only when
 *    `isDefaultDiscount === false` (Special discount mode).
 */
export interface AgreementServiceFee {
  id?: string;
  agreementId?: string;
  serviceId?: string;
  fee?: number;
}

export interface BillingGroupRef {
  id?: string;
  category?: string;
  system?: string;
}

export interface BillingGroupPercentage {
  discount?: number | null;
  billingGroup?: BillingGroupRef;
}

export interface AgreementDetails {
  isActive?: boolean;
  agreementName?: string;
  agreementId?: string;
  numberOfVisitsCalls?: number;
  /** No-Z ISO timestamps on the wire. */
  startDate?: string;
  endDate?: string;
  /** `true` → single org-wide `discountPercent` applies. `false` → use `billingGroupPercentage[]`. */
  isDefaultDiscount?: boolean;
  /** 0–100. */
  discountPercent?: number | null;
  billingGroupPercentage?: BillingGroupPercentage[];
  /** Service IDs explicitly excluded from coverage. */
  excludedServiceIds?: string[];
  /** Service IDs covered with pre-approval required. */
  approvalServiceIds?: string[];
  /** Per-service fee overrides. */
  agreementServiceFees?: AgreementServiceFee[];
  /**
   * Per-specialty discount overrides — populated for Patient Package
   * agreements (Flutter `PatientPackageAgreementDetails.specialtyDiscount`).
   * Unused for Insurance / Corporate. See [[SpecialtyDiscount]].
   */
  specialtyDiscount?: SpecialtyDiscount[];
}

/**
 * Per-specialty discount row inside a Patient Package agreement. Mirrors
 * Flutter `SpecialtyDiscount` (`patient_package_model.dart:114`).
 *
 * `specialty.code.displayName` is the human label; `specialty.code.code` is
 * the FHIR-shaped code. Picker source: `useSpecialityValueset()` —
 * dispenses entries from the org's speciality value set.
 */
export interface SpecialtyDiscount {
  discount?: number | null;
  specialty?: {
    specialitySkillId?: string;
    code?: CodeDisplayName;
  };
}

export interface PayerDetails {
  id?: string;
  imageUrl?: string;
  payerName?: string;
  payerCompanyId?: string;
  /** Dotted-code form like `INS010_GN`. */
  payerAgreementId?: string;
  payerAgreementName?: string;
  office?: string;
  address?: string;
  website?: string;
  email?: string;
  phoneNumber?: string;
  note?: string;
  relatedTpa?: TpaModel[];
}

export interface Payer {
  id?: string;
  organizationId?: string;
  payerDetails?: PayerDetails;
  agreementDetails?: AgreementDetails;
  isArchived?: boolean;
  createdOn?: string;
  createdById?: string;
  updatedOn?: string;
  updatedById?: string;
  /** `'Insurance' | 'Corporate' | 'Patient'`. */
  agreementType?: string;
  /** GUID of the linked service list. */
  serviceListId?: string;
  serviceListName?: string;
  /** Wire field present on read responses; not always echoed back. */
  listType?: string;
}

/** Response wrapper for `GET /api/Billing/Agreements/GetAll`. */
export interface PayerListResponse {
  pageNumber?: number;
  pageSize?: number;
  totalNumberOfPages?: number;
  totalNumberOfRecords?: number;
  results?: Payer[];
}

/**
 * Picker entry from `GET /api/Billing/Agreements/AvailableInsurance`.
 * Picking one auto-fills the payer details section on create.
 */
export interface AvailableInsurance {
  id?: string;
  payerDetails?: PayerDetails;
  isCustom?: boolean;
  isArchived?: boolean;
  createdOn?: string;
  createdById?: string;
  updatedOn?: string;
  updatedById?: string;
}

// ─── Corporate Payers (CorporateAgreements) ──────────────────────────────────

/**
 * Corporate Payer — employer-funded agreement. Mirrors Flutter
 * `coporate_agreement_model.dart` (Flutter has a typo, fixed here).
 *
 * Wire shape is identical to {@link Payer} on the read path: same
 * `payerDetails` + `agreementDetails` + Coverage fields. Differences from
 * Insurance live in the *create* flow — Corporate has no master-list
 * picker (payer name is a plain text field) and no `relatedTpa` linkage.
 *
 * `agreementType` is `'Corporate'` on the wire. List filter:
 * `GET /api/Billing/Agreements/GetAll?type=Corporate`.
 */
export type CorporatePayer = Payer;

/** Response wrapper for `GET /api/Billing/Agreements/GetAll?type=Corporate`. */
export interface CorporatePayerListResponse {
  pageNumber?: number;
  pageSize?: number;
  totalNumberOfPages?: number;
  totalNumberOfRecords?: number;
  results?: CorporatePayer[];
}

// ─── Patient Packages (PatientAgreements) ────────────────────────────────────

/**
 * Patient Package agreement details. Mirrors Flutter
 * `PatientPackageAgreementDetails` (`patient_package_model.dart:58`).
 *
 * Distinct from Insurance / Corporate {@link AgreementDetails} in two ways:
 *  - Adds `numberOfVisitsCalls` (visit budget per package).
 *  - Adds `specialtyDiscount[]` (per-specialty overrides — used when the
 *    discount mode is Special). Replaces / extends the billing-group grid.
 *  - Coverage is **excluded-only** — no `approvalServiceIds` and no
 *    `agreementServiceFees`. A service is either included or excluded.
 */
export interface PatientPackageAgreementDetails {
  isActive?: boolean;
  agreementName?: string;
  /** Free-text package ID (max 20 char). NOT a GUID. */
  agreementId?: string;
  numberOfVisitsCalls?: number | null;
  /** No-Z ISO timestamps on the wire. */
  startDate?: string;
  endDate?: string;
  isDefaultDiscount?: boolean;
  discountPercent?: number | null;
  billingGroupPercentage?: BillingGroupPercentage[];
  specialtyDiscount?: SpecialtyDiscount[];
  excludedServiceIds?: string[];
}

/**
 * Patient Package — prepaid bundle (visit count + discount). Mirrors
 * Flutter `PatientPackage`. Unlike Insurance / Corporate, there is **no
 * `payerDetails`** — the package is owned by the org, not a third party.
 *
 * `agreementType` is `'Patient'` on the wire (NOT `'PatientPackage'`).
 * List filter: `GET /api/Billing/Agreements/GetAll?type=Patient`.
 */
export interface PatientPackage {
  id?: string;
  organizationId?: string;
  agreementDetails?: PatientPackageAgreementDetails;
  isArchived?: boolean;
  createdOn?: string;
  createdById?: string;
  updatedOn?: string;
  updatedById?: string;
  /** `'Patient'` on the wire. */
  agreementType?: string;
}

/** Response wrapper for `GET /api/Billing/Agreements/GetAll?type=Patient`. */
export interface PatientPackageListResponse {
  pageNumber?: number;
  pageSize?: number;
  totalNumberOfPages?: number;
  totalNumberOfRecords?: number;
  results?: PatientPackage[];
}

// ─── Remittance Advice (PROD-701) ────────────────────────────────────────────
//
// Surface: `/billing/remittance-advice`. Backend at
// `/api/Billing/RemittanceAdvice/RemittanceAdvice/*` (yes, doubled path
// segment — controller/resource convention, both Flutter + swagger agree).
// Two flows: Add (validate XML → create) and Process (one-step modal).
// No SignalR on this surface.

/**
 * Body for `POST /api/Billing/RemittanceAdvice/RemittanceAdvice`.
 * Backend type: `RemittanceAdviceRequest`.
 *
 * `allocation` is accepted by the backend but Flutter's filter widget
 * has it commented out (`remittance_advice_filter.dart:411-525`) and
 * v1 React mirrors that. Carry the slot in the type so a future ticket
 * can enable the dimension without a type change.
 */
export interface RemittanceAdviceListRequest {
  /** No-Z ISO. Defaults to last 30 days from today. */
  startDate?: string;
  endDate?: string;
  /** `'Pending'`, `'Processed'`. */
  status?: string[];
  /** `'Auto-allocated'`, `'File'` — unused by v1 UI (Flutter parity). */
  allocation?: string[];
  /** Sponsor GUIDs. */
  sponsors?: string[];
}

/** Facet entry shared with other billing facets. */
export interface RemittanceFacetEntry {
  id?: string;
  name?: string;
  imageUrl?: string | null;
}

/**
 * Facet block on the list response.
 *
 * ⚠ `allocationes` is the literal wire-key from the .NET response and
 * NOT a typo on our side. Same pattern as `isurance` on the Claims
 * rollup totals (PROD-699). Do NOT "fix" to `allocations` — the .NET
 * model binder is case + spelling sensitive on this property name, and
 * "correcting" it makes the facet array silently undefined at runtime.
 * Verified live against the local Billing API at
 * `localhost:6012/swagger/v1/swagger.json` 2026-05-15 (the swagger
 * declares the field as untyped `object`, but the live response
 * confirms the key spelling).
 */
export interface RemittanceAdviceFacets {
  statuses?: RemittanceFacetEntry[];
  /**
   * ⚠ Wire JSON key: `allocationes` (typo preserved — see interface
   * docblock above). Any future TS rename via IDE refactor needs to
   * leave the runtime JSON key as `"allocationes"`.
   */
  allocationes?: RemittanceFacetEntry[];
  sponsors?: RemittanceFacetEntry[];
}

/**
 * One row in the Remittance Advice list. Mirrors Flutter `RemittanceAdvice`.
 * Backend OpenAPI calls it `RemittanceAdviceDto`.
 */
export interface RemittanceAdviceRow {
  id?: string;
  /** `'Pending'` | `'Processed'`. */
  status?: string;
  /** `'Auto-allocated'` | `'File'`. */
  allocation?: string;
  sponsor?: RemittanceFacetEntry;
  claimCount?: number;
  paymentAmount?: number;
  /** No-Z ISO. */
  remittanceDate?: string;
  paymentReference?: string;
}

/** Response from the list endpoint (backend `FacetedPagedResults<RemittanceAdviceDto>`). */
export interface RemittanceAdviceListResponse {
  facets?: RemittanceAdviceFacets;
  pageNumber?: number;
  pageSize?: number;
  totalNumberOfPages?: number;
  totalNumberOfRecords?: number;
  results?: RemittanceAdviceRow[];
}

/**
 * Response from `POST /InvoiceClaim/RemittanceAdvice/ValidateXml`
 * (multipart upload). Backend swagger declares `BaseResponse` but
 * Flutter actually parses this richer shape and reads `file.requestId`
 * to chain into the Create call. Flutter has shipped in prod for years
 * — trust its parsing over the swagger declaration. See CONVERSION_NOTES
 * (`docs/conversion-notes/PROD-701-remittance-advice.md`) §2 for the
 * follow-up backend ticket to align swagger with reality.
 */
export interface ValidateRemittanceXmlResponse {
  file?: {
    /** Original file name supplied at upload. */
    name?: string;
    /** Opaque GUID to carry into `CreateRemittanceAdviceRequest.fileRequestId`. */
    requestId?: string;
  };
  /** `'Success'` on validate, `'Failed'` on parse / schema error. */
  status?: string;
  /** Validation error messages — empty array on Success. */
  errors?: string[];
}

/**
 * Body for `POST /InvoiceClaim/RemittanceAdvice/RemittanceAdvice/Create`.
 * Backend type: `CreateRemittanceAdviceRequest`.
 *
 * `status` is hardcoded to `'Pending'` by the Flutter form — every newly-
 * created remittance starts Pending until processed.
 */
export interface CreateRemittanceAdviceRequest {
  /** No-Z ISO — user-picked. */
  remittanceDate: string;
  paymentAmount: number;
  /** Omitted on create — Flutter sends `null` (the Add widget never sets it). */
  status?: string;
  /** Sponsor GUID. */
  sponsor: string;
  paymentReference?: string;
  /** Auto-allocate by Xero payment matching — UI checkbox. */
  isAutoAllocate?: boolean;
  /** GUID from `ValidateRemittanceXmlResponse.file.requestId`. */
  fileRequestId?: string;
}

/**
 * Body for `POST /InvoiceClaim/RemittanceAdvice/RemittanceAdvice/{id}/Process`.
 * Backend type: `ProcessRemittanceRequest`.
 */
export interface ProcessRemittanceRequest {
  /** No-Z ISO. */
  paymentReceivedDate?: string;
  paymentReferenceNumber?: string;
  /** Whether the payment was actually received (vs flagged as not received). */
  isReceived?: boolean;
}

/**
 * Response from `GET /InvoiceClaim/RemittanceAdvice/RemittanceAdvice/Download/DHPOSample`.
 * NOTE the field is `xmlLink` here — every other export endpoint in the
 * billing surface uses `csvLink`. Backend wire shape; don't normalise.
 */
export interface RemittanceXmlSampleResponse {
  xmlLink?: string;
}

// ─── Reconciliation (PROD-823 — beta UI, fixture-only) ───────────────────────
//
// Surface: `/billing/reconciliation`. Frontend-only port — Flutter renders
// from a hardcoded mock at `mock_data.dart:600-621`; backend has no
// reconciliation endpoints yet (verified against
// `localhost:6012/swagger/v1/swagger.json` 2026-05-18). When the backend
// ships, the wire shape codified below should hold.

/**
 * One reconciliation row. Mirrors Flutter `ClaimReconciliation`
 * (`claim_reconciliation_data.dart:42`).
 *
 * TODO(PROD-823): wire-key mismatch landmine for the eventual backend
 * port. The Flutter Dart property `writeOffAmount` deserialises from
 * the JSON key `"writeOff"` (`:94`). When the backend lands real
 * endpoints, mirror that mapping — either rename this TS property to
 * `writeOff` to match the wire, or keep `writeOffAmount` and add a
 * Zod/mapping layer to translate at the fetch boundary. Search for
 * `TODO(PROD-823)` to find every spot that needs reconciling. Full
 * context in `docs/conversion-notes/PROD-823-reconciliation.md`.
 */
export interface ReconciliationRow {
  id: number;
  /** Sponsor name as a flat string (NOT a `{id,name,imageUrl}` object — Flutter parity). */
  sponsor: string;
  netAmountTotal: number;
  paidAmountTotal: number;
  /** Note the camel-cased `OutStanding` — Flutter property name preserved. */
  outStandingAmount: number;
  /**
   * TODO(PROD-823): wire JSON key will be `"writeOff"` (NOT `writeOffAmount`)
   * once backend lands — see Flutter `claim_reconciliation_data.dart:94`.
   * v1 fixture uses `writeOffAmount` everywhere because there's no wire
   * round-trip yet. Rename or add a mapper before the first network call.
   */
  writeOffAmount: number;
  writeOffReason: string;
}

// ---------------------------------------------------------------------------
// Order to Invoice (PROD-2583 / PROD-2594): pending orders from Assessment & Plan.
// Mirrors pm-react `src/shared/types/billing.ts` (PROD-2590 + PROD-2630); there is no shared package.
//
//   GET  /api/Billing/Invoice/{invoiceId}/Orders
//   POST /api/Billing/Invoice/{invoiceId}/Orders/{add|dismiss|restore|revert}
//
// Manual only: an order never moves onto the invoice by itself. The biller adds
// it by hand or dismisses it. Dismissing never cancels the clinical order.
// ---------------------------------------------------------------------------

export type InvoiceOrderType = 'Lab' | 'Procedure' | 'Vaccine';

/** A vaccine fans out into a product line and, when a priced 90471 exists at the
 *  location, an administration line. Labs and procedures are `Service`. */
export type InvoiceOrderChargeRole = 'Service' | 'VaccineProduct' | 'VaccineAdministration';

/** `Priced`: exactly one service matched, `serviceId` + `indicativePrice` set.
 *  `Ambiguous`: several matched, pick one of `serviceCandidates`.
 *  `NoPrice`: none matched, pick any service at the location. */
export type InvoiceOrderPriceState = 'Priced' | 'Ambiguous' | 'NoPrice';

export type InvoiceOrderStatus = 'Pending' | 'Added' | 'Dismissed';

export type InvoiceOrderDismissReason =
  | 'NotPerformed'
  | 'BilledElsewhere'
  | 'IncludedInPackage'
  | 'Other';

export type InvoiceOrdersState = 'Ok' | 'NoEncounter';

export type InvoiceOrdersReadOnlyReason = 'Issued' | 'Cancelled';

export interface InvoiceOrderUserRef {
  id: string;
  name: string;
}

export interface InvoiceOrderDiagnosis {
  code: string;
  display: string;
}

export interface InvoiceOrderServiceCandidate {
  id: string;
  code: string;
  codeSystem: string;
  description: string;
  fee: number;
}

export interface InvoiceOrderLine {
  /** Stable key for the order line; every POST addresses rows by it. */
  sourceOrderKey: string;
  orderType: InvoiceOrderType;
  chargeRole: InvoiceOrderChargeRole;
  code: string;
  serviceCode: string | null;
  codeSystem: string | null;
  display: string;
  /** Empty on a removed-from-plan added row. */
  diagnoses: InvoiceOrderDiagnosis[];
  /** False when the plan is unsigned, and on a removed-from-plan added row. */
  signed: boolean;
  priceState: InvoiceOrderPriceState;
  /** Set only when `priceState === 'Priced'`. */
  serviceId: string | null;
  serviceCandidates: InvoiceOrderServiceCandidate[];
  /** Set only when `priceState === 'Priced'`. Never part of the invoice totals. */
  indicativePrice: number | null;
  status: InvoiceOrderStatus;
  /** The invoice line this order became, when `status === 'Added'`. */
  invoiceDetailsId: string | null;
  addedBy: InvoiceOrderUserRef | null;
  addedOn: string | null;
  dismissedBy: InvoiceOrderUserRef | null;
  dismissedAt: string | null;
  dismissReason: InvoiceOrderDismissReason | null;
  dismissNote: string | null;
  /** True when the order was added to the invoice and later left the plan. */
  orderRemovedFromPlan: boolean;
  /**
   * PROD-2630: the clinical order's own status as the plan reports it
   * (e.g. "Requested", "On hold", "Sample collected", "Result received").
   * Information only: nothing on the invoice moves with it. Always null for
   * procedures, and for an added line whose order left the plan.
   *
   * Deliberately `string`, not a union: Billing maps the plan's status codes to
   * a label and may add labels; the UI only displays it, so a new label must
   * render rather than fail to type-check. Branch on `orderStatusCode` if a
   * behaviour ever depends on the status.
   */
  orderStatus?: string | null;
  /** PROD-2630: the raw status code behind `orderStatus` (e.g. "active"). Not displayed. */
  orderStatusCode?: string | null;
  /**
   * PROD-2630: who placed the order. Null when there is no performer or the
   * order left the plan; `name` is null when the lookup failed.
   */
  orderedBy?: { id: string; name: string | null } | null;
  // ---- added from pm-react (PROD-3312) ----
  /**
   * PROD-2848: what a lab line is, when its order has a requisition. `Test` /
   * `Panel` lines of a clinic panel carry `labPanelName`; `Reflex` /
   * `Unsolicited` are tests the lab performed. Null for every other order.
   * Patient's choice of lab orders never arrive: Billing leaves them out.
   * PROD-2858: claims carry CPT codes only, so a clinic panel is always one
   * CPT line per test; there is no `Package` or `PackageCap` line any more.
   */
  lineKind?: InvoiceOrderLineKind | null;
  /** PROD-2848: always true since PROD-2858 (no Package cap line). Absent = true. */
  canAdd?: boolean;
  /**
   * PROD-2858: a self-pay clinic panel line's share of the package discount
   * (positive), in proportion to its price, so the panel's lines total the
   * package price. Add puts it on the invoice line as its own discount.
   */
  packageDiscount?: number | null;
  /** PROD-2858: "Package discount" when `packageDiscount` is set. */
  discountReason?: string | null;
  /** PROD-2858: `indicativePrice` less `packageDiscount`. */
  netIndicativePrice?: number | null;
  /** PROD-2858: an insured patient's lab line with no CPT code. Staff decide at Add. */
  notClaimable?: boolean;
  /** PROD-2858: "No CPT: not claimable" when `notClaimable`. */
  notClaimableReason?: string | null;
  /** PROD-2848: the clinic panel a line came from. */
  labPanelName?: string | null;
  /** PROD-2848: the lab the requisition goes to. */
  labVendorName?: string | null;
  labRequestId?: string | null;
  labRequisitionId?: string | null;
  labPanelId?: string | null;
  labPanelMemberId?: string | null;
}

export interface InvoiceOrders {
  invoiceId: string;
  encounterId: string | null;
  /** `NoEncounter` for device bookings: the section is hidden. */
  state: InvoiceOrdersState;
  canEdit: boolean;
  readOnlyReason: InvoiceOrdersReadOnlyReason | null;
  currency: string;
  /** Sum of the pending indicative prices. Never summed with the invoice totals. */
  pendingIndicativeTotal: number;
  pending: InvoiceOrderLine[];
  added: InvoiceOrderLine[];
  dismissed: InvoiceOrderLine[];
  // ---- added from pm-react (PROD-3312) ----
  /**
   * PROD-2858: who pays (`Invoice.SponsorType`). Only a SelfPay invoice gets
   * package discounts; an Insurance invoice flags lab lines with no CPT.
   */
  payer?: 'SelfPay' | 'Insurance' | 'Corporate' | string | null;
}

export interface InvoiceOrderItemRef {
  sourceOrderKey: string;
}

export interface InvoiceOrderAddItem extends InvoiceOrderItemRef {
  /** Required for Ambiguous (one of `serviceCandidates`) and NoPrice (any
   *  service at the location). Optional for Priced, and must then match. */
  serviceId?: string;
}

/** `'PreClaim'` when opened from the Claim Workbench, as on the existing
 *  add/remove-service PUT. */
export type InvoiceOrdersUpdateType = 'PreClaim' | null;

export interface AddInvoiceOrdersRequest {
  /** All or nothing, at most 100. */
  items: InvoiceOrderAddItem[];
  updateType?: InvoiceOrdersUpdateType;
}

export interface DismissInvoiceOrdersRequest {
  items: InvoiceOrderItemRef[];
  reason?: InvoiceOrderDismissReason;
  /** At most 500 characters. */
  note?: string;
}

export interface RestoreInvoiceOrdersRequest {
  items: InvoiceOrderItemRef[];
}

export interface RevertInvoiceOrdersRequest {
  items: InvoiceOrderItemRef[];
  updateType?: InvoiceOrdersUpdateType;
}

/** Every POST returns the re-priced invoice plus the fresh orders read. */
export interface InvoiceOrdersMutationResponse {
  invoice: InvoiceDetail;
  orders: InvoiceOrders;
}

export type InvoiceOrdersErrorCode =
  // 400
  | 'InvalidRequest'
  | 'InvoiceNotDraft'
  | 'AddNotAllowed'
  | 'RemoveNotAllowed'
  // 404
  | 'InvoiceNotFound'
  // 409
  | 'AlreadyAdded'
  | 'NotPending'
  | 'NotDismissed'
  | 'NotAdded'
  | 'ServiceAlreadyOnInvoice'
  // 422
  | 'NoEncounter'
  | 'OrderNotInPlan'
  | 'ServiceRequired'
  | 'ServiceNotCandidate'
  | 'ServiceNotAvailable';

export interface InvoiceOrdersErrorItem {
  sourceOrderKey: string;
  code: string;
  message: string;
}

/** Error body of every Orders endpoint. */
export interface InvoiceOrdersError {
  code: InvoiceOrdersErrorCode | string;
  message: string;
  items: InvoiceOrdersErrorItem[];
}

// ---------------------------------------------------------------------------
// Added from pm-react src/shared/types/billing.ts (PROD-3312 appointment Billing tab).
// ---------------------------------------------------------------------------

export type InvoiceBillableSource = 'Manual';

/** PROD-2848: `InvoiceOrderLine.lineKind`. */
export type InvoiceOrderLineKind = 'Test' | 'Panel' | 'Reflex' | 'Unsolicited';

/**
 * A management (invoice-wide) discount, as returned by
 * `GET /api/Billing/ManagementDiscount/{invoiceId}` (backend `ManagementDiscountDto`).
 * Deleted via `PUT /api/Billing/ManagementDiscount/Delete` with `{ discountId }`.
 */
export interface ManagementDiscountServiceDetail {
  id?: string;
  amount?: number;
  invoiceDetailsId?: string | null;
}

export interface ManagementDiscount {
  id: string;
  invoiceId?: string;
  /** 'Amt' = flat amount · 'Pct' = percentage. */
  type?: string;
  amount?: number;
  reason?: string;
  createdOn?: string;
  isInvoiceIssued?: boolean;
  serviceDiscounts?: ManagementDiscountServiceDetail[];
}

/**
 * One service's slice of a grant (PROD-2151). The allocation is snapshotted at
 * sale: `allocatedTotal` is this service's share of the price paid, and
 * `refundableValue` is what the unused units of it are worth.
 */
export interface PackageGrantServiceLine {
  serviceId: string;
  unitsPurchased: number;
  unitsUsed: number;
  unitsRemaining: number;
  allocatedTotal: number;
  consumedValue: number;
  refundableValue: number;
}

export interface PackageGrant {
  id: string;
  patientId: string;
  packageId: string;
  packageCode?: string | null;
  packageName?: string | null;
  unitsPurchased: number;
  unitsUsed: number;
  unitsRemaining: number;
  valuePerUnit: number;
  expiresOn?: string | null;
  grantedOn: string;
  source: string;
  sourcePaymentId?: string | null;
  isRedeemable: boolean;
  coveredServiceIds: string[];
  /** Sum of referencePrice x quantity at sale (PROD-2151). */
  referenceValue?: number | null;
  /** referenceValue - price paid. */
  saving?: number | null;
  /**
   * Sum of the per-service refundable values. Optional and absent on grants
   * sold before the per-service model: those fall back to
   * `unitsRemaining x valuePerUnit`, as the backend does.
   */
  refundableValue?: number | null;
  /** Empty or absent on pre-PROD-2151 grants. */
  services?: PackageGrantServiceLine[];
}

export interface PackageRedemption {
  id: string;
  packageGrantId?: string | null;
  membershipPeriodAllowanceId?: string | null;
  settlementSource: 'PackageGrant' | 'Membership' | string;
  invoiceDetailsId: string;
  unitsConsumed: number;
  amount: number;
  consumedOn: string;
}

export type PackagePaymentMethod = 'Cash' | 'Card' | 'Bank';

/** Body for POST /Billing/PackageGrants. */
export interface SellPackageRequest {
  patientId: string;
  packageId: string;
  paymentMethod: PackagePaymentMethod;
  /** No-Z ISO; null = never expires. */
  expiresOn?: string | null;
  note?: string | null;
  /** No-Z ISO local wall-clock; omitted = server now in the user's timezone. */
  receiptDate?: string | null;
}

export interface SellPackageResponse {
  grantId: string;
  receiptId?: string | null;
  unitsPurchased: number;
  valuePerUnit: number;
  expiresOn?: string | null;
}

/** Body for POST /Billing/PackageGrants/{grantId}/refund. */
export interface RefundPackageGrantRequest {
  refundTo: PackagePaymentMethod;
  /** Master-data refund reason; `value` is the reason's display text, as the receipt refund dialog sends. */
  refundReason: { id: string; value?: string | null };
}

export interface RefundPackageGrantResponse {
  grantId: string;
  refundedUnits: number;
  refundedAmount: number;
  refundId?: string | null;
  /** What came back per service (PROD-2151); absent on legacy grants. */
  services?: Array<{ serviceId: string; unitsRefunded: number; amount: number }>;
}

/** One consumption row against a grant; reversed rows are returned too. */
export interface PackageGrantRedemptionHistory {
  id: string;
  invoiceId: string;
  invoiceNumber?: string | null;
  appointmentId?: string | null;
  encounterId?: string | null;
  providerId?: string | null;
  invoiceDetailsId: string;
  serviceId?: string | null;
  unitsConsumed: number;
  amount: number;
  /** No-Z ISO. */
  consumedOn: string;
  /** No-Z ISO; set when the invoice was cancelled and the units handed back. */
  reversedOn?: string | null;
}
